import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LIST_LIMIT,
  RunSequence,
  State,
} from "react-lib/apps/MEH/sequence/WellnessOAAccessGrant";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import formatComma from "react-lib/utils/formatComma";
import { Table } from "react-lib/frameworks/Table";
import { formatDatetime } from "react-lib/utils/dateUtils";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Header,
  Icon,
  Input,
  Modal,
  Tab,
  TabProps,
  TextArea,
} from "semantic-ui-react";

// UX

// Utils
import { useIntl } from "react-intl";

// CSS
import "./CardWellnessOAAccessGrant.scss";
import { color } from "html2canvas/dist/types/css/types/color";

// Types
export type CardWellnessOAAccessGrantProps = {
  setProp: (key: string, value: any) => any;
  onEvent: (e: any) => any;
  // seq
  runSequence: RunSequence;
  // data
  WellnessOAAccessGrantSequence: State["WellnessOAAccessGrantSequence"];
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  masterData?: Record<string, any[]>;
  masterOptions?: Record<string, any[]>;
  divisionHasUserItems?: Record<string, any>[];
  selectedDivision?: Record<string, any>;
  languageUX?: string;
};

const CARD_WELLNESS_OA_ACCESS_GRANT = "CardWellnessOAAccessGrant";

const CardWellnessOAAccessGrant = (props: CardWellnessOAAccessGrantProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [openModal, setOpenModal] = useState("");

  useEffect(() => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "SEARCH",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  const divisionOption = useMemo(() => {
    return (props.divisionHasUserItems || []).map((item) => {
      return {
        key: item?.division?.id,
        value: item?.division?.id,
        text: item?.division?.name,
      };
    });
  }, [props.divisionHasUserItems]);

  const handleActionButton = (status: string, item: any) => {
    if (status === "Reject") {
      props.setProp(`WellnessOAAccessGrantSequence.actionReject`, item);
      setOpenModal("ActionsReject");
    } else if (status === "Cancel") {
      props.setProp(`WellnessOAAccessGrantSequence.actionCancel`, item);
      setOpenModal("ActionsCancel");
    } else if (status === "Confirm") {
      props.runSequence({
        sequence: "WellnessOAAccessGrant",
        action: "CONFIRM_ACCESS",
        item: item,
        card: CARD_WELLNESS_OA_ACCESS_GRANT,
      });
    }
  };

  const ownerAccessList = useMemo(() => {
    return (props.WellnessOAAccessGrantSequence?.ownerAccessList?.items || []).map((item) => {
      return {
        ...item,
        datetime: <div>{formatDatetime(item.datetime)}</div>,
        status_label: (
          <div>
            <div style={{ color: statusColor[item.status], fontWeight: "bold" }}>
              {item.status_label}
            </div>
            {item.remark && (
              <div style={{ display: "flex", gap: 10, color: "#666" }}>
                <div>หมายเหตุ: </div>
                <div>{item.remark}</div>
              </div>
            )}
          </div>
        ),
        action: (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            {(item.allowed_actions || []).map((data: string) => {
              const action = allowedActions[data];

              if (!action) return null;

              const buttonKey = `${CARD_WELLNESS_OA_ACCESS_GRANT}_${action.button}_${item.id}`;

              return (
                <ButtonLoadCheck
                  key={buttonKey}
                  setProp={props.setProp}
                  paramKey={buttonKey}
                  buttonLoadCheck={props.buttonLoadCheck?.[buttonKey]}
                  onClick={() => handleActionButton(data, item)}
                  style={{
                    backgroundColor: action.color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                  size="mini"
                  title={action.label}
                />
              );
            })}
          </div>
        ),
      };
    });
  }, [props.WellnessOAAccessGrantSequence?.ownerAccessList]);

  const granteerAccessList = useMemo(() => {
    return (props.WellnessOAAccessGrantSequence?.granteerAccessList?.items || []).map((item) => {
      return {
        ...item,
        datetime: <div>{formatDatetime(item.datetime)}</div>,
        status_label: (
          <div>
            <div style={{ color: statusColor[item.status], fontWeight: "bold" }}>
              {item.status_label}
            </div>
            {item.remark && (
              <div style={{ display: "flex", gap: 10, color: "#666" }}>
                <div>หมายเหตุ: </div>
                <div>{item.remark}</div>
              </div>
            )}
          </div>
        ),
        action: (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            {(item.allowed_actions || []).map((data: string) => {
              const action = allowedActions[data];

              if (!action) return null;

              const buttonKey = `${CARD_WELLNESS_OA_ACCESS_GRANT}_${action.button}_${item.id}`;

              return (
                <ButtonLoadCheck
                  key={buttonKey}
                  setProp={props.setProp}
                  paramKey={buttonKey}
                  buttonLoadCheck={props.buttonLoadCheck?.[buttonKey]}
                  onClick={() => handleActionButton(data, item)}
                  style={{
                    backgroundColor: action.color,
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                  size="mini"
                  title={action.label}
                />
              );
            })}
          </div>
        ),
      };
    });
  }, [props.WellnessOAAccessGrantSequence?.granteerAccessList]);

  const panes = useMemo(
    () => [
      {
        menuItem: {
          key: "owner",
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <span>คำขอที่ได้รับ</span>
              <div className="badge">
                {props.WellnessOAAccessGrantSequence?.ownerAccessList?.total || 0}
              </div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <div>
              <Table
                data={ownerAccessList || []}
                headers="ผู้ส่งคำขอ,ผู้รับคำขอ,สิทธิ,วันเวลา,สถานะ,Action"
                keys="grantee_division_name,owner_division_name,access_type_label,datetime,status_label,action"
                minRows={9}
                showPagination={false}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", padding: "15px 0px" }}>
              <PaginationShorthand
                activePage={props?.WellnessOAAccessGrantSequence?.ownerAccessList?.activePage || 1}
                limit={DEFAULT_LIST_LIMIT}
                total={props?.WellnessOAAccessGrantSequence?.ownerAccessList?.total || 0}
                onPageChange={(activePage) =>
                  props.runSequence({
                    sequence: "WellnessOAAccessGrant",
                    action: "SEARCH",
                    card: CARD_WELLNESS_OA_ACCESS_GRANT,
                    ownerActivePage: activePage,
                    granteeActivePage:
                      props?.WellnessOAAccessGrantSequence?.granteerAccessList?.activePage || 1,
                  })
                }
              />
            </div>
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "granteer",
          content: (
            <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
              <span>คำขอที่ส่งไป</span>
              <div className="badge">
                {props.WellnessOAAccessGrantSequence?.granteerAccessList?.total || 0}
              </div>
            </div>
          ),
        },
        render: () => (
          <Tab.Pane>
            <div>
              <Table
                data={granteerAccessList || []}
                headers="ผู้ส่งคำขอ,ผู้รับคำขอ,สิทธิ,วันเวลา,สถานะ,Action"
                keys="grantee_division_name,owner_division_name,access_type_label,datetime,status_label,action"
                minRows={9}
                showPagination={false}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", padding: "15px 0px" }}>
              <PaginationShorthand
                activePage={
                  props?.WellnessOAAccessGrantSequence?.granteerAccessList?.activePage || 1
                }
                limit={DEFAULT_LIST_LIMIT}
                total={props?.WellnessOAAccessGrantSequence?.granteerAccessList?.total || 0}
                onPageChange={(activePage) =>
                  props.runSequence({
                    sequence: "WellnessOAAccessGrant",
                    action: "SEARCH",
                    card: CARD_WELLNESS_OA_ACCESS_GRANT,
                    ownerActivePage:
                      props?.WellnessOAAccessGrantSequence?.ownerAccessList?.activePage || 1,
                    granteeActivePage: activePage,
                  })
                }
              />
            </div>
          </Tab.Pane>
        ),
      },
    ],
    [ownerAccessList, granteerAccessList],
  );

  const handleTabChange = (e: SyntheticEvent, data: TabProps) => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "SEARCH",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      ownerActivePage: 1,
      granteeActivePage: 1,
      status: "ALL",
    });

    setActiveTabIndex(data.activeIndex as number);
  };

  const handleCreateAccess = () => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "CREATE_ACCESS",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      onCloes: () => {
        setOpenModal("");
      },
    });
  };

  const handleRejectAccess = () => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "REJECT_ACCESS",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      onCloes: () => {
        setOpenModal("");
      },
    });
  };

  const handleCancelAccess = () => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "CANCEL_ACCESS",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      onCloes: () => {
        setOpenModal("");
      },
    });
  };

  const handleChangePage = (activePage: number) => {
    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "SEARCH",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
      activePage,
    });
  };

  const handleChangeFilterData = async (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    await props.setProp(`WellnessOAAccessGrantSequence.filterWellnessOA.${name}`, value);

    props.runSequence({
      sequence: "WellnessOAAccessGrant",
      action: "SEARCH",
      card: CARD_WELLNESS_OA_ACCESS_GRANT,
    });
  };

  const handleChangeActionCreate = (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    if (name === "access_type") {
      const accessTypes = props.WellnessOAAccessGrantSequence?.actionCreate?.access_type || [];

      const newAccessTypes = data.checked
        ? accessTypes.includes(value)
          ? accessTypes
          : [...accessTypes, value]
        : accessTypes.filter((item: string) => item !== value);

      props.setProp(`WellnessOAAccessGrantSequence.actionCreate.${name}`, newAccessTypes);
    } else {
      props.setProp(`WellnessOAAccessGrantSequence.actionCreate.${name}`, value);
    }
  };

  const handleChangeActionReject = (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    props.setProp(`WellnessOAAccessGrantSequence.actionReject.${name}`, value);
  };

  const handleChangeActionCancel = (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    props.setProp(`WellnessOAAccessGrantSequence.actionCancel.${name}`, value);
  };

  return (
    <div
      className="card-wellness-oa-access-grant"
      style={{
        padding: "15px 10px",
        height: "calc(105vh)",
        overflow: "auto",
        paddingBottom: "3rem",
      }}
    >
      <SnackMessage
        error={props.errorMessage?.[CARD_WELLNESS_OA_ACCESS_GRANT]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_WELLNESS_OA_ACCESS_GRANT}`, null);
        }}
      />
      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          padding: "0 15px",
        }}
      >
        <div style={{ fontSize: "1.15rem", fontWeight: "bold" }}>
          สิทธิการเข้าถึง Official Account
        </div>
      </div>

      <div className="access-grant-actions">
        <div className="access-grant-filter">
          <Icon name="filter" style={{ color: "#B7D7E2" }} />
          <span>สถานะ:</span>

          <Dropdown
            placeholder="เลือกสถานะ"
            selection
            clearable
            options={statusOptions}
            value={props.WellnessOAAccessGrantSequence?.filterWellnessOA?.status}
            name="status"
            onChange={handleChangeFilterData}
          />
        </div>

        <ButtonLoadCheck
          setProp={props.setProp}
          onClick={() => setOpenModal("ActionsNew")}
          style={{
            background: "var(--primary-theme-color)",
            color: "white",
          }}
          icon="plus"
          size="small"
          title="สร้างคำขอสิทธิ"
        />
      </div>

      <div className="access-grant-toolbar">
        <Tab
          className="access-grant-tabs"
          activeIndex={activeTabIndex}
          menu={{ pointing: true, secondary: true }}
          panes={panes}
          onTabChange={handleTabChange}
        />
      </div>

      <Modal open={openModal === "ActionsNew"} size={"small"}>
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "var(--primary-theme-color)",
              color: "#ffffff",
              padding: "15px 20px",
            }}
          >
            <div style={{ fontSize: "1.15rem", fontWeight: "bold" }}>
              <label>สร้างคำขอสิทธิ</label>
            </div>
            <div style={{ color: "#ffffff" }}>
              <Icon name="close icon" onClick={() => setOpenModal("")} />
            </div>
          </div>
          <div style={{ display: "flex", margin: "20px" }}>
            <div style={{ width: "100%" }}>
              <label>ผู้ส่งคำขอ</label>
              <div style={{ margin: "10px 0px" }}>
                <Dropdown
                  fluid
                  selection
                  options={divisionOption}
                  value={props.selectedDivision?.id}
                  disabled
                />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", margin: "20px" }}>
            <div style={{ width: "100%" }}>
              <label>ผู้รับคำขอ (ค้นหาหน่วยงาน / Official Account)</label>
              <div style={{ margin: "10px 0px" }}>
                <Dropdown
                  fluid
                  clearable
                  selection
                  options={divisionOption}
                  value={props.WellnessOAAccessGrantSequence?.actionCreate?.owner_division}
                  name="owner_division"
                  onChange={handleChangeActionCreate}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              margin: "20px",
            }}
          >
            <div style={{ width: "100%" }}>
              <label>สิทธิที่ต้องการขอ</label>
              <div
                style={{
                  margin: "10px 0px",
                  padding: "10px 0px",
                  border: "1px solid rgba(117, 117, 117, 0.5)",
                  borderRadius: "6px",
                }}
              >
                <div style={{ padding: "5px 10px" }}>
                  <Checkbox
                    checked={props.WellnessOAAccessGrantSequence?.actionCreate?.access_type?.includes(
                      "PATIENT",
                    )}
                    label="ข้อมูลผู้รับบริการ"
                    name="access_type"
                    value="PATIENT"
                    onChange={handleChangeActionCreate}
                  />
                </div>

                <div style={{ padding: "5px 10px" }}>
                  <Checkbox
                    checked={props.WellnessOAAccessGrantSequence?.actionCreate?.access_type?.includes(
                      "PRODUCT",
                    )}
                    label="ข้อมูลสินค้า/Package"
                    name="access_type"
                    value="PRODUCT"
                    onChange={handleChangeActionCreate}
                  />
                </div>
                <div style={{ padding: "5px 10px" }}>
                  <Checkbox
                    checked={props.WellnessOAAccessGrantSequence?.actionCreate?.access_type?.includes(
                      "MAKE_APPOINTMENT",
                    )}
                    label="นัดหมายกับ Official Account"
                    name="access_type"
                    value="MAKE_APPOINTMENT"
                    onChange={handleChangeActionCreate}
                  />
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px" }}>
            <div>
              <Button
                onClick={() => setOpenModal("")}
                style={{
                  background: "white",
                  color: "rgba(117, 117, 117)",
                  border: "1px solid rgba(117, 117, 117, 0.5)",
                }}
                size="small"
              >
                ยกเลิก
              </Button>
            </div>
            <div>
              <ButtonLoadCheck
                setProp={props.setProp}
                onClick={handleCreateAccess}
                paramKey={`${CARD_WELLNESS_OA_ACCESS_GRANT}_CREATE_ACCESS`}
                buttonLoadCheck={
                  props.buttonLoadCheck?.[`${CARD_WELLNESS_OA_ACCESS_GRANT}_CREATE_ACCESS`]
                }
                style={{
                  background: "var(--primary-theme-color)",
                  color: "white",
                }}
                size="small"
                title="สร้างคำขอสิทธิ"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={openModal === "ActionsReject"} size={"small"}>
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "#F21651",
              color: "#ffffff",
              padding: "15px 20px",
            }}
          >
            <div style={{ display: "flex", gap: 10 }}>
              <div>
                <Icon name="exclamation triangle" />
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: "bold" }}>
                <label>ยืนยันการปฏิเสธคำขอ (Reject)</label>
              </div>
            </div>

            <div style={{ color: "#ffffff" }}>
              <Icon name="close icon" onClick={() => setOpenModal("")} />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              margin: "20px",
            }}
          >
            <div
              style={{
                margin: "10px 0px",
                padding: "10px 0px",
                border: "1px solid rgba(117, 117, 117, 0.5)",
                borderRadius: "6px",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", gap: 10, padding: "5px 10px" }}>
                <label style={{ fontWeight: "bold" }}>หน่วยงาน: </label>
                <label>
                  {props.WellnessOAAccessGrantSequence?.actionReject?.owner_division_name}
                </label>
              </div>

              <div style={{ display: "flex", gap: 10, padding: "5px 10px" }}>
                <label style={{ fontWeight: "bold" }}>สิทธิที่ดำเนินการ: </label>
                <label>
                  {props.WellnessOAAccessGrantSequence?.actionReject?.access_type_label}
                </label>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", margin: "20px" }}>
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex" }}>
                <label>ระบุเหตุผล</label>
                <label style={{ color: "red" }}>*</label>
              </div>
              <div style={{ margin: "10px 0px" }}>
                <TextArea
                  rows={4}
                  style={{
                    width: "100%",
                    resize: "none",
                    padding: "10px",
                  }}
                  placeholder="โปรดระบุเหตุผลในการปฏิเสธ หรือยกเลิกคำขอ"
                  value={props.WellnessOAAccessGrantSequence?.actionReject?.reason}
                  name="reason"
                  onChange={handleChangeActionReject}
                ></TextArea>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px" }}>
            <div>
              <Button
                onClick={() => setOpenModal("")}
                style={{
                  background: "white",
                  color: "rgba(117, 117, 117)",
                  border: "1px solid rgba(117, 117, 117, 0.5)",
                }}
                size="small"
              >
                ยกเลิก
              </Button>
            </div>
            <div>
              <ButtonLoadCheck
                setProp={props.setProp}
                onClick={handleRejectAccess}
                paramKey={`${CARD_WELLNESS_OA_ACCESS_GRANT}_REJECT_ACCESS`}
                buttonLoadCheck={
                  props.buttonLoadCheck?.[`${CARD_WELLNESS_OA_ACCESS_GRANT}_REJECT_ACCESS`]
                }
                style={{
                  background: "#F21651",
                  color: "white",
                }}
                size="small"
                title="ยืนยันปฏิเสธ"
              />
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={openModal === "ActionsCancel"} size={"small"}>
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "#D96523",
              color: "#ffffff",
              padding: "15px 20px",
            }}
          >
            <div style={{ display: "flex", gap: 10 }}>
              <div>
                <Icon name="exclamation triangle" />
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: "bold" }}>
                <label>ยืนยันการยกเลิกคำขอ / สิทธิ (Cancel)</label>
              </div>
            </div>

            <div style={{ color: "#ffffff" }}>
              <Icon name="close icon" onClick={() => setOpenModal("")} />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              margin: "20px",
            }}
          >
            <div
              style={{
                margin: "10px 0px",
                padding: "10px 0px",
                border: "1px solid rgba(117, 117, 117, 0.5)",
                borderRadius: "6px",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", gap: 10, padding: "5px 10px" }}>
                <label style={{ fontWeight: "bold" }}>หน่วยงาน: </label>
                <label>
                  {props.WellnessOAAccessGrantSequence?.actionCancel?.owner_division_name}
                </label>
              </div>

              <div style={{ display: "flex", gap: 10, padding: "5px 10px" }}>
                <label style={{ fontWeight: "bold" }}>สิทธิที่ดำเนินการ: </label>
                <label>
                  {props.WellnessOAAccessGrantSequence?.actionCancel?.access_type_label}
                </label>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", margin: "20px" }}>
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex" }}>
                <label>ระบุเหตุผล</label>
                <label style={{ color: "red" }}>*</label>
              </div>
              <div style={{ margin: "10px 0px" }}>
                <TextArea
                  rows={4}
                  style={{
                    width: "100%",
                    resize: "none",
                    padding: "10px",
                  }}
                  placeholder="โปรดระบุเหตุผลในการปฏิเสธ หรือยกเลิกคำขอ"
                  value={props.WellnessOAAccessGrantSequence?.actionCancel?.reason}
                  name="reason"
                  onChange={handleChangeActionCancel}
                ></TextArea>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px" }}>
            <div>
              <Button
                onClick={() => setOpenModal("")}
                style={{
                  background: "white",
                  color: "rgba(117, 117, 117)",
                  border: "1px solid rgba(117, 117, 117, 0.5)",
                }}
                size="small"
              >
                ยกเลิก
              </Button>
            </div>
            <div>
              <ButtonLoadCheck
                setProp={props.setProp}
                onClick={handleCancelAccess}
                paramKey={`${CARD_WELLNESS_OA_ACCESS_GRANT}_CANCEL_ACCESS`}
                buttonLoadCheck={
                  props.buttonLoadCheck?.[`${CARD_WELLNESS_OA_ACCESS_GRANT}_CANCEL_ACCESS`]
                }
                style={{
                  background: "#D96523",
                  color: "white",
                }}
                size="small"
                title="ยืนยันยกเลิก"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

CardWellnessOAAccessGrant.displayName = "CardWellnessOAAccessGrant";

export default React.memo(CardWellnessOAAccessGrant);

const statusOptions = [
  { key: "ALL", text: "ทั้งหมด", value: "ALL" },
  { key: "REQUESTED", text: "Request", value: "REQUESTED" },
  { key: "REJECTED", text: "Reject", value: "REJECTED" },
  { key: "CONFIRMED", text: "Confirm", value: "CONFIRMED" },
];

const statusColor: Record<string, string> = {
  REQUESTED: "#D96523",
  REJECTED: "#F21651",
  CONFIRMED: "#038C5A",
  CANCELED: "#666",
};

const allowedActions: Record<string, { label: string; color: string; button: string }> = {
  Request: {
    label: "Request",
    color: "yellow",
    button: "REQUEST_ACCESS",
  },
  Confirm: {
    label: "Confirm",
    color: "#038C5A",
    button: "CONFIRM_ACCESS",
  },
  Reject: {
    label: "Reject",
    color: "#F21651",
    button: "REJECT_ACCESS",
  },
  Cancel: {
    label: "Cancel",
    color: "#D96523",
    button: "CANCEL_ACCESS",
  },
};
